import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams } from 'react-router';
import dayjs from 'dayjs';

import { actions } from 'features/users/redux';
import { actions as authActions } from 'features/Auth';

import { balanceSortTypes, usernameSortTypes } from '../data';

const useUsersModel = () => {
  const dispatch = useDispatch();

  const { tab } = useParams();

  const { id } = useSelector(state => state.auth, shallowEqual);
  const { isNeedToRefresh } = useSelector(state => state.users, shallowEqual);
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const { selectedAgentId, agentClickedTrigger } = useSelector(state => state.tree, shallowEqual);

  const firstRender = useRef(true);

  const [filterValue, setFilterValue] = useState('');
  const [count, setCount] = useState(10);
  const [actionProcessing, changeActionProcessing] = useState(false);
  const [page, setPage] = useState(0);
  const [itemsOnPage, setItemsOnPage] = useState({ value: 10, name: '10' });
  const [date, setDate] = useState({
    beginDate: null,
    endDate: null,
  });
  const [isDirectStructure, setDirectStructure] = useState(false);

  const [balanceSortType, setBalanceSortType] = useState({
    ...balanceSortTypes.at(-1),
    name: `(${locale[balanceSortTypes.at(-1).name]})`,
  });

  const [usernameSortType, setUsernameSortType] = useState({
    ...usernameSortTypes.at(-1),
    name: `(${locale[usernameSortTypes.at(-1).name]})`,
  });

  const role = useMemo(() => {
    if (tab === 'players' || tab === 'referrals') {
      setPage(0);
      return 0;
    }
    if (tab === 'agents') {
      setPage(0);
      return 6;
    }
    if (tab === 'all' || tab === 'disabled') {
      setPage(0);
      return '';
    }
  }, [tab]);

  const isBanned = useMemo(() => {
    if (tab === 'disabled') {
      return true;
    } else return false;
  }, [tab]);

  const isReferral = useMemo(() => {
    if (tab === 'referrals') {
      return true;
    } else return undefined;
  }, [tab]);

  const selectItemChangedHandler = itemValue => {
    setItemsOnPage({ value: itemValue, name: itemValue });
    onItemsOnPageChanged(itemValue);
  };

  const onSetBalanceSort = type => {
    const match = balanceSortTypes.find(el => el.value === type);
    setBalanceSortType({ ...match, name: `(${locale[match.name]})` });

    const match2 = usernameSortTypes.at(-1);
    setUsernameSortType({ ...match2, name: `(${locale[match2.name]})` });
  };

  const onSetUsernameSort = type => {
    const match = usernameSortTypes.find(el => el.value === type);
    setUsernameSortType({ ...match, name: `(${locale[match.name]})` });

    const match2 = balanceSortTypes.at(-1);
    setBalanceSortType({ ...match2, name: `(${locale[match2.name]})` });
  };

  const onChangeFilterValue = e => {
    setFilterValue(e.currentTarget.value);
  };

  const onItemsOnPageChanged = newItemsOnPage => {
    setCount(newItemsOnPage);
    setPage(0);
  };

  const onChangeBeginDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate({ ...date, beginDate: value });
    }
  };

  const onChangeEndDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate({ ...date, endDate: value });
    }
  };

  const onResetFilter = () => {
    setFilterValue('');
    setDate({
      beginDate: null,
      endDate: null,
    });
    getUserWithFilter({
      username: '',
      date_from: '',
      date_to: '',
    });
  };

  const onChangeDirectStructure = value => {
    setDirectStructure(value);
  };

  const getUserWithFilter = (data = {}, withName) => {
    let orderBy = null;
    if (balanceSortType.value === 0 && usernameSortType.value === 0) orderBy = null;
    else if (balanceSortType.value === 0 && usernameSortType.value !== 0) orderBy = usernameSortType.value;
    else if (balanceSortType.value !== 0 && usernameSortType.value === 0) orderBy = balanceSortType.value;

    dispatch(
      actions.getUsersList(
        {
          username: filterValue,
          role,
          userId: selectedAgentId || id,
          isBanned,
          isReferral,
          count,
          page,
          order_by: orderBy /* balanceSortType.value === 0 ? null : balanceSortType.value */,
          date_from: date.beginDate ? dayjs(date.beginDate).format('YYYY-MM-DD') : '',
          date_to: date.endDate ? dayjs(date.endDate).format('YYYY-MM-DD') : '',
          isDirectStructure,
          ...data,
        },
        withName,
      ),
    );
  };

  const getTreeByUsername = () => {
    changeActionProcessing(true);
    getUserWithFilter(
      {
        // userId: !filterValue ? selectedAgentId ?? id : undefined,
        userId: selectedAgentId ?? id,
      },
      true,
    );
    // .then(user => {
    //   if (user?.id) {
    //     dispatch(
    //       treeActions.getUsersByUserId(
    //         { id: user.id, nesting: [user.id], name: user.username },
    //         true
    //       )
    //     )
    //     .finally(() => {
    //       dispatch(treeActions.selectAgentId(user.id));
    //       if (filterValue) {
    //         setFilterValue('');
    //         getUserWithFilter({ username: '', userId: user.id });
    //       }

    //       changeActionProcessing(false);
    //     });
    //   } else {
    //     changeActionProcessing(false);
    //   }
    // });
    changeActionProcessing(false);
  };

  useEffect(() => {
    if (!firstRender.current) {
      onResetFilter();
    }

    firstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentClickedTrigger]);

  useEffect(() => {
    getTreeByUsername();
    dispatch(authActions.getCashbackInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isBanned, isReferral, id, page, itemsOnPage.value, isNeedToRefresh, balanceSortType, usernameSortType]);

  return {
    filterValue,
    page,
    date,
    itemsOnPage,
    balanceSortType,
    usernameSortType,
    role,
    actionProcessing,
    isBanned,
    onChangeFilterValue,
    onChangeBeginDate,
    onChangeEndDate,
    onResetFilter,
    onSetBalanceSort,
    onSetUsernameSort,
    onItemsOnPageChanged,
    setPage,
    setBalanceSortType,
    setUsernameSortType,
    selectItemChangedHandler,
    getTreeByUsername,
    getUserWithFilter,
    onChangeDirectStructure,
    isDirectStructure,
  };
};

export default useUsersModel;
